import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import StudentFormDialog from "@/components/students/StudentFormDialog";
import { STUDENT_STATUSES, STATUS_STYLE, PAYMENT_STYLE, fmtMoney } from "@/lib/studentConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Plus, Search, Download, Loader2, GraduationCap, Eye, Users, CheckCircle2, Award, Activity,
} from "lucide-react";

function Kpi({ icon: Icon, label, value, tint, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tint}`}><Icon className="h-4 w-4" /></span>
      </div>
      <p className="mt-2 font-display text-2xl font-bold tracking-tight text-secondary">{value}</p>
    </div>
  );
}

export default function Students() {
  const navigate = useNavigate();
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [opts, setOpts] = useState({ courses: [], batches: [], trainers: [] });
  const [filters, setFilters] = useState({ search: "", status: "all", course: "all", batch: "all", trainer: "all", mode: "all" });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const t = setTimeout(() => { setFilters((f) => ({ ...f, search: searchInput })); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students", { params: { ...filters, page, page_size: pageSize } });
      setResult({ items: data.items, total: data.total });
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  const fetchAux = useCallback(async () => {
    const [s, o] = await Promise.all([api.get("/students/dashboard"), api.get("/students/filters")]);
    setStats(s.data); setOpts(o.data);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  const saveStudent = async (payload, id) => {
    try {
      if (id) { await api.put(`/students/${id}`, payload); toast.success("Student updated"); }
      else { await api.post("/students", payload); toast.success("Student created"); }
      fetchList(); fetchAux();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save student");
      throw err;
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/students/export", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `sifcore_students_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const totalPages = Math.max(1, Math.ceil(result.total / pageSize));

  return (
    <div data-testid="students-page">
      <PageHeader
        title="Student Management"
        subtitle="Manage admissions, batches, training and placements."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportExcel} data-testid="export-students-button"><Download className="mr-1.5 h-4 w-4" /> Export</Button>
            <Button onClick={() => setFormOpen(true)} data-testid="add-student-button"><Plus className="mr-1.5 h-4 w-4" /> Add Student</Button>
          </div>
        }
      />

      {stats && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi testid="stat-total" icon={Users} label="Total Students" value={stats.total_students} tint="bg-primary/10 text-primary" />
            <Kpi testid="stat-active" icon={Activity} label="Active" value={stats.active_students} tint="bg-sky-500/10 text-sky-600" />
            <Kpi testid="stat-completed" icon={CheckCircle2} label="Completed" value={stats.completed_students} tint="bg-accent/10 text-accent" />
            <Kpi testid="stat-placed" icon={Award} label="Placed" value={stats.placed_students} tint="bg-violet-500/10 text-violet-600" />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5" data-testid="chart-course">
              <h3 className="mb-3 font-display text-sm font-semibold text-secondary">Students by Course</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.students_by_course} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="course" tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#F1F5F9" }} />
                  <Bar dataKey="count" fill="#2563EB" radius={[5, 5, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border border-border bg-card p-5" data-testid="chart-trainer">
              <h3 className="mb-3 font-display text-sm font-semibold text-secondary">Students by Trainer</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.students_by_trainer} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="trainer" tick={{ fontSize: 11, fill: "#64748B" }} width={90} />
                  <Tooltip cursor={{ fill: "#F1F5F9" }} />
                  <Bar dataKey="count" fill="#22C55E" radius={[0, 5, 5, 0]} maxBarSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, mobile, email, ID..." className="pl-9" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} data-testid="students-search" />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger className="w-full sm:w-36" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem>{STUDENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.course} onValueChange={(v) => setFilter("course", v)}>
          <SelectTrigger className="w-full sm:w-44" data-testid="filter-course"><SelectValue placeholder="Course" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Courses</SelectItem>{opts.courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.batch} onValueChange={(v) => setFilter("batch", v)}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-batch"><SelectValue placeholder="Batch" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Batches</SelectItem>{opts.batches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.trainer} onValueChange={(v) => setFilter("trainer", v)}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-trainer"><SelectValue placeholder="Trainer" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Trainers</SelectItem>{opts.trainers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : result.items.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="students-empty">
            <GraduationCap className="h-8 w-8" />
            <p>No students found. Convert a lead or add one manually.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Student ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden md:table-cell">Course</TableHead>
                  <TableHead className="hidden lg:table-cell">Batch</TableHead>
                  <TableHead className="hidden xl:table-cell">Trainer</TableHead>
                  <TableHead className="hidden sm:table-cell">Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.items.map((s) => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`} className="cursor-pointer hover:bg-slate-50 hover:text-slate-900" onClick={() => navigate(`/students/${s.id}`)}>
                    <TableCell className="font-mono text-xs text-slate-500">{s.student_code}</TableCell>
                    <TableCell>
                      <div className="font-medium text-secondary">{s.full_name}</div>
                      <div className="text-xs text-muted-foreground">{s.mobile}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600">{s.course_enrolled || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600">{s.batch || "—"}</TableCell>
                    <TableCell className="hidden xl:table-cell text-slate-600">{s.trainer || "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell"><Badge className={`${PAYMENT_STYLE[s.payment_status]} border-0`}>{s.payment_status}</Badge></TableCell>
                    <TableCell><Badge className={`${STATUS_STYLE[s.status]} border-0`}>{s.status}</Badge></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); navigate(`/students/${s.id}`); }} data-testid={`view-student-${s.id}`}><Eye className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between border-t border-border px-4 py-3" data-testid="pagination">
              <p className="text-sm text-muted-foreground">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, result.total)} of {result.total}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page">Previous</Button>
                <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">Next</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <StudentFormDialog open={formOpen} onOpenChange={setFormOpen} student={null} onSave={saveStudent} />
    </div>
  );
}
